import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import {
  InfoCard,
  Link,
  Progress,
  WarningPanel,
} from '@backstage/core-components';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@material-ui/core';
import { relativeTime } from '../releases/relativeTime';
import { releaseDeployment } from '../releases/deploymentState';
import { ReleaseDeploymentChip } from '../releases/ReleaseDeploymentChip';
import { useDeploymentStates } from '../releases/useDeploymentStates';

type FeedRelease = {
  entityRef: string;
  tagName: string;
  commitSha?: string;
  releasedAt: string;
  webUrl?: string;
};

/** Components shown, one row each. */
const COMPONENTS_SHOWN = 5;

/**
 * The feed is fetched deeper than it is shown, because it is ordered by time and
 * one component releasing five times in an afternoon would otherwise be the
 * whole card. Fifty rows is comfortably enough to find five distinct components
 * in this catalog.
 */
const FEED_DEPTH = 50;

/**
 * The latest release of each of the five most recently released components.
 *
 * Collapsed by component rather than by time: "what has shipped lately" is a
 * question about the platform, and a list where four of five rows are the same
 * service answers it badly. Each row also says what became of that release, so
 * the card distinguishes shipped from actually running.
 */
export const LatestReleasesCard = () => {
  const discoveryApi = useApi(discoveryApiRef);
  const { fetch } = useApi(fetchApiRef);

  const { value, loading, error } = useAsync(async (): Promise<
    FeedRelease[]
  > => {
    const baseUrl = await discoveryApi.getBaseUrl('releases');
    const response = await fetch(`${baseUrl}/?limit=${FEED_DEPTH}`);
    if (!response.ok) {
      const body = await response.json().catch(() => undefined);
      throw new Error(
        body?.error ??
          `Request failed with ${response.status} ${response.statusText}`,
      );
    }
    return (await response.json()).releases ?? [];
  }, []);

  // The feed is newest first, so the first row seen for a component is its
  // latest release.
  const newestPerComponent: FeedRelease[] = [];
  const seen = new Set<string>();
  for (const release of value ?? []) {
    if (seen.has(release.entityRef)) {
      continue;
    }
    seen.add(release.entityRef);
    newestPerComponent.push(release);
    if (newestPerComponent.length === COMPONENTS_SHOWN) {
      break;
    }
  }

  const { states } = useDeploymentStates(
    newestPerComponent.map(release => release.entityRef),
  );

  let content;
  if (loading) {
    content = <Progress />;
  } else if (error) {
    content = (
      <WarningPanel title="Could not load releases" message={error.message} />
    );
  } else if (newestPerComponent.length === 0) {
    content = (
      <Typography variant="body2" color="textSecondary">
        No releases have been ingested yet.
      </Typography>
    );
  } else {
    content = (
      <Table size="small">
        <TableBody>
          {newestPerComponent.map(release => (
            <TableRow key={release.entityRef}>
              <TableCell>
                <EntityRefLink entityRef={release.entityRef} />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {release.webUrl ? (
                    <Link to={release.webUrl}>{release.tagName}</Link>
                  ) : (
                    release.tagName
                  )}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  component="span"
                  title={new Date(release.releasedAt).toLocaleString()}
                >
                  {relativeTime(release.releasedAt)}
                </Typography>
              </TableCell>
              <TableCell>
                <ReleaseDeploymentChip
                  deployment={releaseDeployment({
                    release,
                    state: states[release.entityRef],
                    // The whole fetched feed, not just the rows shown: a
                    // superseded release is named after the release that
                    // replaced it wherever that release is known, and it is
                    // usually one of the rows this card left out. Otherwise the
                    // chip reads "replaced by b9889cf0", which is true and tells
                    // a reader nothing.
                    releases: value,
                  })}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <InfoCard
      title="Latest releases"
      deepLink={{ title: 'All releases', link: '/releases' }}
    >
      {content}
    </InfoCard>
  );
};
